/**
 * SpeakerLabel — a color-coded pill/badge showing speaker name.
 * Confidence maps to opacity. Source affects border style.
 * Adapted from live-tranz/src/components/speaker/SpeakerLabel.tsx
 */

import { splitProps } from 'solid-js'
import { getSpeakerColor } from './speakerColors'

export interface SpeakerLabelProps {
  label: string
  confidence?: number
  source?: string
  class?: string
  onClick?: (e: MouseEvent | undefined) => void
}

/**
 * Color-coded pill/badge showing speaker name.
 * Confidence maps to opacity. Border style varies by source.
 */
export function SpeakerLabel(props: SpeakerLabelProps) {
  const [local] = splitProps(props, [
    'label',
    'confidence',
    'source',
    'class',
    'onClick',
  ])

  const resolvedColor = () => getSpeakerColor(local.label)
  const opacity = () => {
    const c = local.confidence
    if (c === undefined) return 1
    return 0.5 + c * 0.5 // range 0.5–1.0
  }

  const borderStyle = () => {
    switch (local.source) {
      case 'manual':
        return 'border-solid'
      case 'server':
        return 'border-dashed'
      default:
        return 'border-dotted'
    }
  }

  return (
    <span
      onClick={(e) => local.onClick?.(e)}
      class={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border cursor-pointer transition-all select-none whitespace-nowrap hover:brightness-110 ${borderStyle()} ${local.class || ''}`}
      style={{
        'background-color': `color-mix(in srgb, ${resolvedColor()} 15%, transparent)`,
        'border-color': resolvedColor(),
        'color': resolvedColor(),
        'opacity': opacity(),
      }}
      title={`${local.label}${
        local.confidence !== undefined
          ? ` (${Math.round(local.confidence * 100)}%)`
          : ''
      }${
        local.source ? ` [${local.source}]` : ''
      }`}
    >
      <span
        class="w-2 h-2 rounded-full flex-shrink-0"
        style={{ 'background-color': resolvedColor() }}
      />
      <span class="truncate max-w-[10rem]">{local.label}</span>
    </span>
  )
}
